import * as Sentry from "@sentry/nextjs";
import { FormikHelpers } from "formik/dist/types";
import { TFunction, useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import React, {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  FC,
  useCallback,
  useMemo,
  useState,
} from "react";
import * as yup from "yup";

import { CryptoCurrencyEnum } from "@/consts/CryptoCurrencyEnum";
import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { FormikDialog } from "@/features/common/components/dialog/FormikDialog";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useNftContext } from "@/features/nft/hooks/useNft";
import { useMasonryOpenSeaAssets } from "@/features/post/components/utils/InfiniteMasonryReducer";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import { getCryptoPriceLimit } from "@/helpers/priceLimit/getPriceLimit";

const CurrencySelectionField = dynamic(
  () => import("@/features/common/components/formik/CurrencySelectionField"),
  {
    ssr: false,
  },
);

type PutOnSaleButtonProps = {
  loading?: boolean;
  fullWidth?: boolean;
  isNFTOwner: boolean;
  tokenId: string;
  postId: string;
  onSale?: (startAmount: number) => Promise<void>;
  buttonProps?: Omit<
    DetailedHTMLProps<
      ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >,
    "onClick"
  >;
};

type PutOnSaleInput = {
  cryptoCurrency: CryptoCurrencyEnum;
  cryptoPrice: number;
};

//TODO: support other crypto currencies
/** supporting ETH only
 * @params onConfirm get fn putOnSale from useOpenSeaAsset
 */
export const PutOnSaleButton: FC<PutOnSaleButtonProps> = (props) => {
  const {
    loading = false,
    buttonProps: { className, ...otherButtonProps },
    fullWidth = false,
    isNFTOwner,
    tokenId,
    postId,
    onSale,
  } = props;
  const { t } = useTranslation("common");

  // get current user
  const { user } = useUserContext();
  // get current users wallet info & nft api
  const {
    activeAccount,
    seaport,
    connectOrOnBoard,
    isSameWallet,
    isSameNetwork,
  } = useNftContext();
  // get the contractAddress
  const {
    service: {
      openSea: { contractAddress },
    },
  } = getPublicConfig();
  // get [openSeaAssetHashmap, dispatch] from OpenSeaAssetsContext
  const [openSeaAssetHashmap, dispatch, refetchSingleAsset] =
    useMasonryOpenSeaAssets();

  // For error display
  const { openSnackbar } = useSnackbar();

  // handle dialog, button disable, button loading state
  const [inputDialogOpen, setInputDialogOpen] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(
    Object.keys(otherButtonProps).includes("disabled")
      ? otherButtonProps.disabled
      : false,
  );
  const [buttonLoading, setButtonLoading] = useState<boolean>(loading);

  /* controlling dialog */
  const onClickButton = () => setInputDialogOpen(true);
  const onClose = () => setInputDialogOpen(false);

  const initialValues = useMemo(() => {
    return { cryptoCurrency: CryptoCurrencyEnum.Eth, cryptoPrice: undefined };
  }, []);

  const putOnSaleSchema = useMemo(() => {
    return getSchema(t);
  }, [t]);

  const onSubmit: (
    values: PutOnSaleInput,
    helpers: FormikHelpers<PutOnSaleInput>,
  ) => void = async (values) => {
    setButtonLoading(true);
    setButtonDisabled(true);
    onSale
      ? await onSale(values.cryptoPrice)
      : await defaultPutOnSale(values.cryptoPrice);
    setButtonLoading(false);
    setButtonDisabled(false);
    setInputDialogOpen(false);
  };

  // function for putOnSale this asset - Owner
  const defaultPutOnSale = useCallback(
    async (startAmount: number) => {
      // if not owner, construct nothing
      if (!isNFTOwner) return;

      // ask user to conenct to wallet if not connected
      if (!activeAccount) {
        return connectOrOnBoard();
      }
      if (!isSameNetwork) {
        return openSnackbar({
          message: t("common:nft.networkMismatch"),
          severity: "error",
        });
      }
      if (!isSameWallet) {
        return openSnackbar({
          message: t("common:nft.pleaseSwitchWallet", {
            walletAccount: user.walletAccount,
          }),
          severity: "error",
        });
      }

      setButtonDisabled(true);
      try {
        await seaport.createSellOrder({
          asset: {
            tokenId,
            tokenAddress: contractAddress,
          },
          accountAddress: activeAccount,
          startAmount: Number(startAmount),
        });
        // update the current asset
        setTimeout(async () => {
          await refetchSingleAsset(tokenId, postId);
          setButtonDisabled(false);
        }, 1500);
      } catch (e) {
        if (e.code === 4001) {
          openSnackbar({
            message: t("common:nft.errors.userDeclineTransaction"),
            severity: "error",
          });
          return;
        }
        openSnackbar({
          message: t("common:nft.errors.transactionFail"),
          severity: "error",
        });
        Sentry.captureException(e, {
          tags: { seaport: "putOnSale" },
          extra: { tokenId },
        });
        setButtonDisabled(false);
      }
    },
    [
      activeAccount,
      connectOrOnBoard,
      contractAddress,
      isNFTOwner,
      isSameWallet,
      openSnackbar,
      seaport,
      t,
      tokenId,
      user,
      postId,
      refetchSingleAsset,
      isSameNetwork,
    ],
  );

  return (
    <>
      <OutlinedButton
        variant={"primary"}
        loading={buttonLoading}
        fullWidth={fullWidth}
        utilityClassName={className}
        buttonProps={{
          ...otherButtonProps,
          disabled: buttonDisabled || buttonLoading,
          onClick: onClickButton,
        }}
      >
        {t("common:nft.putOnSale")}
      </OutlinedButton>
      <FormikDialog<PutOnSaleInput>
        validationSchema={putOnSaleSchema}
        enableReinitialize
        onSubmit={onSubmit}
        showUnsavedAlert={false}
        initialValues={initialValues}
        open={inputDialogOpen}
        onClose={onClose}
        title={t("common:nft.putOnSale")}
        message={
          <CurrencySelectionField
            isCrypto
            placeholder={t("common:formPlaceholder.price")}
            disabled={false}
            currencySelectionName="cryptoCurrency"
            currencyValueName="cryptoPrice"
          />
        }
      />
    </>
  );
};

const getSchema = (t: TFunction) => {
  const requiredErrMsg = t("common:formValidation.required");
  const { min, max } = getCryptoPriceLimit();
  const message = t("common:formValidation.exceedLimit", {
    min,
    max,
  });
  return yup.object().shape({
    cryptoPrice: yup
      .number()
      .nullable(true)
      .required(requiredErrMsg)
      .min(min, message)
      .max(max, message),
  });
};
